/*
 * Copyright (c) 2023 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { memo } from 'react';
import { Divider, Theme, Typography } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { MainBtn, RedMainBtn } from '../../StyledComponents/MainBtn';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
    DelayedDownloadingStatus,
    DelayedDownloadingsListItemDto,
} from '../../../data/api/downloadings/downloadingsClient.typings';
import { createTableData } from '../DelayedDownloadingsConfig';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        modalWrapper: {
            padding: theme.spacing(2),
        },
        infoBlock: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            margin: theme.spacing(1),
        },
        colorBar: {
            backgroundColor: theme.palette.primary.main,
            height: 50,
            width: 5,
            borderRadius: 5,
            marginRight: theme.spacing(2),
        },
        ready: {
            backgroundColor: theme.palette.primary.main,
        },
        error: {
            backgroundColor: theme.palette.error.main,
        },
        infoData: {
            display: 'flex',
            flexDirection: 'column',
        },
        dataLine: {
            display: 'flex',
            flexDirection: 'row',
        },
        value: {
            fontWeight: 600,
            marginLeft: theme.spacing(0.5),
        },
        actions: {
            marginTop: theme.spacing(2),
            justifyContent: 'center',
        },
        button: {
            padding: theme.spacing(0.8, 1, 0.5, 1),
            minWidth: 150,
        },
        dialogContent: {
            paddingTop: `${theme.spacing(1)}!important`,
        },
    }),
);

interface ConfirmDeleteFileModalProps {
    isOpen: boolean;
    fileDto: DelayedDownloadingsListItemDto;
    onClose: () => void;
    onConfirm: () => void;
}

const ConfirmDeleteFileModal = (props: ConfirmDeleteFileModalProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { onConfirm, onClose, isOpen, fileDto } = props;

    const fileTableData = fileDto ? createTableData([fileDto], t)[0] : null;

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <div className={classes.modalWrapper}>
                <DialogTitle>{t('app.confirmDeleteFile')}</DialogTitle>
                {fileDto ? (
                    <DialogContent className={classes.dialogContent}>
                        <Divider />
                        <div className={classes.infoBlock}>
                            <div
                                className={clsx(
                                    classes.colorBar,
                                    fileDto.status === DelayedDownloadingStatus.Ready ? classes.ready : classes.error,
                                )}
                            />
                            <div className={classes.infoData}>
                                <div className={classes.dataLine}>
                                    <Typography variant={'body1'}>{`${t('app.type')}:`}</Typography>
                                    <Typography className={classes.value} variant={'body1'}>
                                        {fileTableData.type}
                                    </Typography>
                                </div>
                                <div className={classes.dataLine}>
                                    <Typography variant={'body1'}>
                                        {`${t('downloadings.downloadingRequestedDate')}:`}
                                    </Typography>
                                    <Typography className={classes.value} variant={'body1'}>
                                        {fileTableData.requestedDate}
                                    </Typography>
                                </div>
                            </div>
                        </div>
                        <Divider />
                    </DialogContent>
                ) : null}
                <DialogActions className={classes.actions}>
                    <MainBtn className={classes.button} variant="contained" onClick={onClose}>
                        {t('app.no')}
                    </MainBtn>
                    <RedMainBtn
                        className={classes.button}
                        variant="contained"
                        onClick={() => {
                            onConfirm();
                            onClose();
                        }}
                    >
                        {t('app.yes')}
                    </RedMainBtn>
                </DialogActions>
            </div>
        </Dialog>
    );
};

export default memo(ConfirmDeleteFileModal);
